import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LegalFooter } from '../components/LegalFooter';
import { BroadcastBanner } from '../components/BroadcastBanner';
import {
  CheckCircle,
  Circle,
  ArrowRight,
  FileText,
  Calendar,
  Target,
  ChevronRight,
  Clock,
  Sparkles,
  BookOpen,
  Settings,
  Compass,
  Zap
} from 'lucide-react';

const STAGE_CONFIG = {
  started: { color: 'bg-muted text-muted-foreground', icon: Circle },
  edge_complete: { color: 'bg-ss-orange/20 text-ss-orange', icon: CheckCircle },
  fear_complete: { color: 'bg-ss-orange/20 text-ss-orange', icon: CheckCircle }, // Legacy support
  core_complete: { color: 'bg-ss-teal/20 text-ss-teal', icon: CheckCircle },
  archetype_complete: { color: 'bg-ss-teal/20 text-ss-teal', icon: CheckCircle }, // Legacy support
  booked: { color: 'bg-green-100 text-green-800', icon: Calendar },
  applied: { color: 'bg-amber-100 text-amber-800', icon: Target },
  accepted: { color: 'bg-emerald-100 text-emerald-800', icon: Sparkles },
};

// Map legacy types to new names
const getAssessmentLabel = (type) => {
  const labels = {
    'fear': 'EDGE Diagnostic',
    'edge': 'EDGE Diagnostic',
    'archetype': 'CORE Diagnostic',
    'core': 'CORE Diagnostic',
  };
  return labels[type] || type;
};

const getAssessmentIcon = (type) => {
  if (type === 'fear' || type === 'edge') {
    return <Zap className="w-5 h-5 text-ss-orange" />;
  }
  return <Compass className="w-5 h-5 text-ss-teal" />;
};

export default function UserDashboardHome() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    document.title = 'Dashboard | PATH by Secret Sauce';
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, profileRes] = await Promise.all([
        axios.get('/api/dashboard'),
        axios.get('/api/user/profile')
      ]);
      setData(dashboardRes.data);
      setUser(profileRes.data.profile);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  const { stage, nextAction, completedAssessments, stats } = data || {};
  const StageIcon = STAGE_CONFIG[stage?.key]?.icon || Circle;

  // Check for both legacy (fear/archetype) and new (edge/core) completions
  const edgeComplete = stats?.fearCompleted > 0 || stats?.edgeCompleted > 0;
  const coreComplete = stats?.archetypeCompleted > 0 || stats?.coreCompleted > 0;

  return (
    <div className="space-y-8" data-testid="user-dashboard-home">
      {/* Broadcast Banner */}
      <BroadcastBanner />

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary/10 to-accent/5 rounded-xl p-6">
        <h1 className="text-2xl font-bold mb-2">
          {user?.isFirstLogin ? 'Welcome' : 'Welcome back'}{user?.firstName ? `, ${user.firstName}` : ''}!
        </h1>
        <p className="text-muted-foreground">
          Track your progress on the PATH journey and continue your leadership development.
        </p>
      </div>

      {/* Current Stage */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5" /> Your PATH Journey
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${STAGE_CONFIG[stage?.key]?.color || 'bg-muted'}`}>
              <StageIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{stage?.label}</h3>
              <p className="text-sm text-muted-foreground">{stage?.description}</p>
            </div>
          </div>
          
          {/* Progress Steps */}
          <div className="mt-6 flex items-center justify-between">
            {[
              { key: 'edge', label: 'EDGE Diagnostic', done: edgeComplete },
              { key: 'core', label: 'CORE Diagnostic', done: coreComplete },
              { key: 'book', label: 'Book Session', done: stats?.hasBooking },
              { key: 'apply', label: 'Apply for PATH', done: stats?.hasApplication },
            ].map((step, i, arr) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.done ? 'bg-ss-teal/20 text-ss-teal' : 'bg-muted text-muted-foreground'}`}>
                    {step.done ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                  </div>
                  <span className="text-xs mt-1 text-center max-w-[80px]">{step.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${step.done ? 'bg-ss-teal/30' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Next Action CTA */}
      {nextAction && (
        <Card className="border-accent/30 bg-accent/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">{nextAction.title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{nextAction.description}</p>
                
                {nextAction.buttons ? (
                  <div className="flex gap-3">
                    {nextAction.buttons.map((btn, i) => (
                      <Button 
                        key={i}
                        variant={btn.variant === 'outline' ? 'outline' : 'default'}
                        onClick={() => window.location.href = btn.link}
                      >
                        {btn.text}
                      </Button>
                    ))}
                  </div>
                ) : nextAction.buttonText && (
                  <Button onClick={() => window.location.href = nextAction.buttonLink}>
                    {nextAction.buttonText} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <button
          onClick={() => window.location.href = '/dashboard/assessments'}
          className="bg-card border border-border rounded-xl p-5 text-left hover:border-accent/50 hover:shadow-md transition-all group"
          data-testid="view-assessments-btn"
        >
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center mb-3 group-hover:bg-accent/20 transition-colors">
            <FileText className="w-5 h-5 text-accent" />
          </div>
          <h3 className="font-semibold mb-1">My Diagnostics</h3>
          <p className="text-sm text-muted-foreground">View your EDGE and CORE results</p>
        </button>
        
        <button
          onClick={() => window.location.href = '/book'}
          className="bg-card border border-border rounded-xl p-5 text-left hover:border-accent/50 hover:shadow-md transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-ss-teal/10 flex items-center justify-center mb-3 group-hover:bg-ss-teal/20 transition-colors">
            <Calendar className="w-5 h-5 text-ss-teal" />
          </div>
          <h3 className="font-semibold mb-1">Book a Session</h3>
          <p className="text-sm text-muted-foreground">Schedule time with a facilitator</p>
        </button>
        
        <button
          onClick={() => window.location.href = '/dashboard/settings'}
          className="bg-card border border-border rounded-xl p-5 text-left hover:border-accent/50 hover:shadow-md transition-all group"
          data-testid="settings-btn"
        >
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3 group-hover:bg-muted/80 transition-colors">
            <Settings className="w-5 h-5 text-muted-foreground" />
          </div>
          <h3 className="font-semibold mb-1">Settings</h3>
          <p className="text-sm text-muted-foreground">Update profile & privacy options</p>
        </button>
      </div>

      {/* Completed Assessments */}
      {completedAssessments && completedAssessments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" /> Completed Diagnostics
            </CardTitle>
            <CardDescription>
              View your diagnostic results and track your progress over time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {completedAssessments.slice(0, 4).map((assessment) => (
                <div 
                  key={assessment.id}
                  className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => window.location.href = `/results/${assessment.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(assessment.type === 'fear' || assessment.type === 'edge') ? 'bg-ss-orange/10' : 'bg-ss-teal/10'}`}>
                      {getAssessmentIcon(assessment.type)}
                    </div>
                    <div>
                      <p className="font-medium">{getAssessmentLabel(assessment.type)}</p>
                      <p className="text-sm text-muted-foreground">
                        {assessment.primaryResult}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <Badge variant="secondary" className="mb-1">
                        <CheckCircle className="w-3 h-3 mr-1" /> Complete
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        {assessment.completedAt ? formatDate(assessment.completedAt) : ''}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
            
            {completedAssessments.length > 4 && (
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={() => window.location.href = '/dashboard/assessments'}
              >
                View All Diagnostics
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legal Footer */}
      <LegalFooter minimal className="pt-8" />
    </div>
  );
}
